import React from 'react';
import { Box, Button, Typography, Fade, Chip, CircularProgress, alpha } from '@mui/material';
import {
  AutoAwesomeRounded as SmartToyIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenInNewIcon,
  FilePresent as FilePresentIcon,
  ThumbUpOutlined as ThumbUpIcon,
  ThumbDownOutlined as ThumbDownIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { BotMessage } from '../hooks/useChatbotState';
import { formatSuggestionLabel } from '../utils/formatSuggestionLabel';
import { ChatActionGroup } from './ChatActionGroup';
import { ChatSuggestionChip } from './ChatSuggestionChip';
import { ChatMessageActions } from './ChatMessageActions';
import { processBotMessage } from '../utils/botMessageProcessor';
import { ConfirmationCard } from './ConfirmationCard';
import { RedirectCard } from './RedirectCard';
import { DownloadCard } from './DownloadCard';
import { ConfirmationDetails } from '../hooks/useChatbotState';

const renderFormattedBotText = (text: string) => {
  if (!text) return null;
  const lines = text.split('\n');

  return lines.map((line, lineIdx) => {
    const isBullet = /^\s*[-*•]\s+/.test(line);
    const cleanLine = isBullet ? line.replace(/^\s*[-*•]\s+/, '') : line;

    const parts = cleanLine.split(/(\*\*[^*]+\*\*)/g);

    const renderedParts = parts.map((part, partIdx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <Box
            component="span"
            key={partIdx}
            sx={{
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            {part.slice(2, -2)}
          </Box>
        );
      }
      return <React.Fragment key={partIdx}>{part}</React.Fragment>;
    });

    if (isBullet) {
      return (
        <Box
          key={lineIdx}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            mt: 0.5,
            mb: 0.5,
            pl: 0.5,
          }}
        >
          <Box
            sx={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              mt: '8px',
              flexShrink: 0,
            }}
          />
          <Typography
            component="span"
            variant="body2"
            sx={{
              fontSize: '13.5px',
              lineHeight: 1.6,
              color: 'text.primary',
            }}
          >
            {renderedParts}
          </Typography>
        </Box>
      );
    }

    return (
      <Typography
        key={lineIdx}
        variant="body2"
        sx={{
          fontSize: '13.5px',
          lineHeight: 1.6,
          color: 'text.primary',
          mb: lineIdx < lines.length - 1 && line.trim() === '' ? 1 : 0.25,
          minHeight: line.trim() === '' ? '8px' : 'auto',
        }}
      >
        {renderedParts}
      </Typography>
    );
  });
};

interface ChatMessagesProps {
  messages: BotMessage[];
  isLoading: boolean;
  isActivating: boolean;
  handleQuickAction: (action: any) => void;
  handleBotStateAction: (action: 'yes' | 'no' | 'open-form', url?: string) => void;
  handleRelatedChipClick: (chip: any) => void;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  onEndConversation: () => void;
  dismissConfirmation: (messageId: string) => void;
  handleEndConversation: () => Promise<void>;
  handleClearChat: () => void;
  handleMissingFieldClick: (field: string) => void;
  handleIntentConfirmation: (confirm: boolean) => void;
  isAwaitingIntentConfirmation: boolean;
  chatbotExtractionLoading: boolean;
  activeConfirmation: ConfirmationDetails | null;
  confirmationLoading: 'confirm' | 'cancel' | null;
  handleConfirmationAction: (confirm: boolean) => void;
}

export const ChatMessages: React.FC<ChatMessagesProps> = React.memo(
  ({
    messages,
    isLoading,
    isActivating: _isActivating,
    handleQuickAction: _handleQuickAction,
    handleBotStateAction,
    handleRelatedChipClick,
    messagesEndRef,
    onEndConversation,
    dismissConfirmation,
    handleEndConversation,
    handleClearChat,
    handleMissingFieldClick,
    handleIntentConfirmation,
    isAwaitingIntentConfirmation,
    chatbotExtractionLoading,
    activeConfirmation,
    confirmationLoading,
    handleConfirmationAction,
  }) => {
    const [localLoading, setLocalLoading] = React.useState<'confirm' | 'cancel' | null>(null);

    const handleLocalConfirm = (action: () => void | Promise<void>) => {
      setLocalLoading('confirm');
      setTimeout(async () => {
        await action();
        setLocalLoading(null);
      }, 200);
    };

    const handleLocalCancel = (action: () => void) => {
      setLocalLoading('cancel');
      setTimeout(() => {
        action();
        setLocalLoading(null);
      }, 200);
    };

    const renderBotStateUI = (message: BotMessage) => {
      const state = message.state?.toUpperCase();
      switch (state) {
        case 'COMPLETE':
        case 'COMPLETED':
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
              <Typography variant="caption" sx={{ fontWeight: 500, color: 'success.main' }}>
                Conversation complete
              </Typography>
            </Box>
          );

        case 'AWAITING_INPUT':
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
              <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600 }}>
                ↳ Waiting for required information
              </Typography>
              {message.missingFields &&
                message.missingFields.length > 0 &&
                renderMissingFieldsChips(message.missingFields)}
            </Box>
          );

        case 'PROCESSING':
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CircularProgress size={12} sx={{ color: '#111111' }} />
              <Typography variant="caption" sx={{ color: '#757575' }}>
                Processing...
              </Typography>
            </Box>
          );

        case 'CANCELLED':
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Typography variant="caption" sx={{ color: '#D32F2F', fontWeight: 500 }}>
                Conversation cancelled
              </Typography>
            </Box>
          );

        case 'EXPIRED':
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Typography variant="caption" sx={{ color: '#757575', fontWeight: 500 }}>
                Conversation session expired
              </Typography>
            </Box>
          );

        case 'AWAITING_CONFIRMATION':
          return (
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => handleBotStateAction('yes')}
                disabled={isLoading}
                sx={{
                  backgroundColor: '#111111',
                  color: 'white',
                  textTransform: 'none',
                  borderRadius: '8px',
                  '&:hover': { backgroundColor: '#222222' },
                  '&:focus-visible': {
                    outline: '2px solid #111111',
                    outlineOffset: '2px',
                  },
                }}
              >
                Yes
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleBotStateAction('no')}
                disabled={isLoading}
                sx={{
                  borderColor: '#EAEAEA',
                  color: '#1A1A1A',
                  textTransform: 'none',
                  borderRadius: '8px',
                  '&:hover': { borderColor: '#B0B0B0', backgroundColor: '#FAFAFA' },
                  '&:focus-visible': {
                    outline: '2px solid #111111',
                    outlineOffset: '2px',
                  },
                }}
              >
                No
              </Button>
            </Box>
          );

        case 'PREFILL_UI':
          return message.uiAction ? (
            <Box sx={{ mt: 1.5 }}>
              <Button
                variant="contained"
                size="small"
                endIcon={<OpenInNewIcon />}
                onClick={() => handleBotStateAction('open-form', message.uiAction!.editUrl)}
                sx={{
                  backgroundColor: '#111111',
                  color: 'white',
                  textTransform: 'none',
                  borderRadius: '8px',
                  mb: 0.5,
                  '&:hover': { backgroundColor: '#222222' },
                  '&:focus-visible': {
                    outline: '2px solid #111111',
                    outlineOffset: '2px',
                  },
                }}
              >
                Open Form
              </Button>
              <Typography variant="caption" sx={{ display: 'block', color: 'gray', fontSize: '10px' }}>
                Form will be pre-filled with suggested data
              </Typography>
            </Box>
          ) : null;

        case 'AWAITING_INTENT_CONFIRMATION':
          return (
            <ChatActionGroup
              actions={[
                {
                  label: 'Yes',
                  icon: ThumbUpIcon,
                  onClick: () => handleIntentConfirmation(true),
                  disabled: isLoading || !isAwaitingIntentConfirmation,
                },
                {
                  label: 'No',
                  icon: ThumbDownIcon,
                  onClick: () => handleIntentConfirmation(false),
                  disabled: isLoading || !isAwaitingIntentConfirmation,
                },
              ]}
            />
          );

        default:
          return null;
      }
    };

    const renderMissingFieldsChips = (fields?: string[]) => {
      if (!fields || fields.length === 0) return null;
      return (
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
          {fields.map(field => (
            <ChatSuggestionChip
              key={field}
              label={formatSuggestionLabel(field)}
              onClick={() => handleMissingFieldClick(field)}
              disabled={isAwaitingIntentConfirmation}
              sx={{
                borderColor: '#E65100',
                backgroundColor: '#FFF8E1',
                '&:hover': {
                  backgroundColor: '#FFE082',
                  borderColor: '#F57C00',
                },
              }}
            />
          ))}
        </Box>
      );
    };

    const renderRelatedChips = (chips?: BotMessage['relatedChips']) => {
      if (!chips || chips.length === 0) return null;

      return (
        <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ width: '100%', color: '#757575', mb: 0.5 }}>
            Related topics:
          </Typography>
          {chips.map(chip => (
            <ChatSuggestionChip
              key={chip.id}
              label={chip.label}
              onClick={() => handleRelatedChipClick(chip)}
              sx={{
                fontSize: '11px',
                height: '30px',
              }}
            />
          ))}
        </Box>
      );
    };

    return (
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          backgroundColor: '#FFFFFF',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 0, 0, 0.1)',
            borderRadius: '10px',
          },
        }}
      >
        {/* Conversation Messages */}
        {messages.map((message, idx) => {
          const isLastMessage = idx === messages.length - 1;
          return (
            <Fade key={message.id} in>
              {message.role === 'user' ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      maxWidth: '75%',
                      '&:hover .message-actions': {
                        opacity: 1,
                        visibility: 'visible',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText',
                        padding: '12px 16px',
                        borderRadius: '12px 12px 2px 12px',
                        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontSize: '13.5px', lineHeight: 1.5, whiteSpace: 'pre-wrap', color: 'inherit' }}
                      >
                        {message.content}
                      </Typography>

                      {message.attachmentPreviews && message.attachmentPreviews.length > 0 && (
                        <Box sx={{ marginTop: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {message.attachmentPreviews.map((file, idx) => (
                            <Chip
                              key={idx}
                              icon={<FilePresentIcon sx={{ color: 'primary.contrastText !important' }} />}
                              label={`${file.name.substring(0, 15)}...`}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: '0.7rem',
                                height: 'auto',
                                padding: '4px 8px',
                                color: 'primary.contrastText',
                                borderColor: 'rgba(255, 255, 255, 0.4)',
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        marginTop: 0.5,
                        flexDirection: 'row',
                      }}
                    >
                      <ChatMessageActions message={message} isUser={true} />
                      <Typography variant="caption" sx={{ marginRight: 0.5, color: '#9E9E9E', fontSize: '11px' }}>
                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: '8px',
                      background: theme =>
                        `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(
                          theme.palette.primary.main,
                          0.85,
                        )} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#FFFFFF',
                      flexShrink: 0,
                      boxShadow: theme => `0 2px 6px ${alpha(theme.palette.primary.main, 0.25)}`,
                      mt: 0.25,
                    }}
                  >
                    <SmartToyIcon sx={{ fontSize: 16 }} />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      maxWidth: { xs: '88%', sm: '82%' },
                      '&:hover .message-actions': {
                        opacity: 1,
                        visibility: 'visible',
                      },
                    }}
                  >
                    {message.state === 'CONFIRM_CLEAR_CHAT' ? (
                      <ConfirmationCard
                        summary="Clear current chat messages?"
                        onConfirm={() => handleLocalConfirm(handleClearChat)}
                        onCancel={() => handleLocalCancel(() => dismissConfirmation(message.id))}
                        isLoading={localLoading}
                        disabled={isLoading || localLoading !== null}
                      />
                    ) : message.state === 'CONFIRM_END_CONVERSATION' ? (
                      <ConfirmationCard
                        summary="End this conversation?"
                        onConfirm={() => handleLocalConfirm(handleEndConversation)}
                        onCancel={() => handleLocalCancel(() => dismissConfirmation(message.id))}
                        isLoading={localLoading}
                        disabled={isLoading || localLoading !== null}
                      />
                    ) : (
                      <>
                        {(() => {
                          const { text: processedText, url } = processBotMessage(message.content);
                          return (
                            <Box
                              onClick={url ? () => window.open(url, '_blank') : undefined}
                              sx={{
                                backgroundColor: theme =>
                                  url
                                    ? alpha(theme.palette.primary.main, 0.14)
                                    : alpha(theme.palette.primary.main, 0.08),
                                color: 'text.primary',
                                padding: '12px 16px',
                                borderRadius: '14px 14px 14px 2px',
                                boxShadow: url
                                  ? (theme: any) => `0px 2px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                  : '0px 1px 4px rgba(0, 0, 0, 0.02)',
                                border: theme =>
                                  url
                                    ? `1px solid ${alpha(theme.palette.primary.main, 0.25)}`
                                    : `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                                cursor: url ? 'pointer' : 'default',
                                '&:hover': url
                                  ? {
                                      backgroundColor: (theme: any) => alpha(theme.palette.primary.main, 0.2),
                                      boxShadow: (theme: any) =>
                                        `0px 4px 16px ${alpha(theme.palette.primary.main, 0.22)}`,
                                      transform: 'translateY(-1px)',
                                    }
                                  : {},
                                transition: 'all 0.2s ease',
                              }}
                            >
                              {/* Link indicator row */}
                              {url && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    mb: 0.75,
                                    opacity: 0.65,
                                  }}
                                >
                                  <LinkIcon sx={{ fontSize: 12, color: 'primary.main' }} />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: '10.5px',
                                      color: 'primary.main',
                                      letterSpacing: '0.3px',
                                      fontWeight: 500,
                                    }}
                                  >
                                    Click to open link
                                  </Typography>
                                </Box>
                              )}

                              {renderFormattedBotText(processedText)}

                              {/* Open in new tab hint */}
                              {url && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    gap: 0.4,
                                    mt: 0.75,
                                    opacity: 0.55,
                                  }}
                                >
                                  <OpenInNewIcon sx={{ fontSize: 11, color: 'primary.main' }} />
                                  <Typography
                                    variant="caption"
                                    sx={{ fontSize: '10px', color: 'primary.main', letterSpacing: '0.2px' }}
                                  >
                                    Opens in new tab
                                  </Typography>
                                </Box>
                              )}

                              {renderBotStateUI(message)}
                              {renderRelatedChips(message.relatedChips)}
                            </Box>
                          );
                        })()}

                        {message.role === 'bot' && message.redirect?.url && (
                          <RedirectCard redirect={message.redirect} disabled={isLoading} />
                        )}

                        {message.role === 'bot' && message.download && (
                          <DownloadCard download={message.download} disabled={isLoading} />
                        )}

                        {message.role === 'bot' && message.confirmation && isLastMessage && activeConfirmation && (
                          <ConfirmationCard
                            summary={message.confirmation.summary}
                            onConfirm={() => handleConfirmationAction(true)}
                            onCancel={() => handleConfirmationAction(false)}
                            isLoading={confirmationLoading}
                            disabled={isLoading || confirmationLoading !== null}
                          />
                        )}

                        {message.role === 'bot' && message.confirmation && (!isLastMessage || !activeConfirmation) && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mt: 1,
                              mb: 1,
                              p: 1.25,
                              borderRadius: '8px',
                              backgroundColor: '#F5F5F7',
                              border: '1px dashed rgba(0, 0, 0, 0.12)',
                              opacity: 0.8,
                            }}
                          >
                            <CheckCircleIcon sx={{ fontSize: 14, color: '#9E9E9E' }} />
                            <Typography
                              variant="caption"
                              sx={{ color: '#757575', fontStyle: 'italic', fontSize: '11px' }}
                            >
                              Action requested: {message.confirmation.summary}
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        marginTop: 0.5,
                        flexDirection: 'row',
                      }}
                    >
                      <Typography variant="caption" sx={{ marginLeft: 0.5, color: '#9E9E9E', fontSize: '11px' }}>
                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                      <ChatMessageActions message={message} isUser={false} />
                    </Box>
                  </Box>
                </Box>
              )}
            </Fade>
          );
        })}

        {/* Loading Indicator */}
        {isLoading && (
          <Fade in>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '8px',
                  backgroundColor: '#111111',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  flexShrink: 0,
                }}
              >
                <SmartToyIcon sx={{ fontSize: 16 }} />
              </Box>
              <Box
                sx={{
                  backgroundColor: '#F5F5F7',
                  padding: '12px 16px',
                  borderRadius: '20px 20px 20px 4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <CircularProgress size={16} sx={{ color: '#111111' }} />
                <Typography variant="caption" sx={{ color: '#757575', fontSize: '12px' }}>
                  Thinking...
                </Typography>
              </Box>
            </Box>
          </Fade>
        )}

        {chatbotExtractionLoading && (
          <Fade in={chatbotExtractionLoading}>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '8px',
                  backgroundColor: '#111111',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  flexShrink: 0,
                }}
              >
                <SmartToyIcon sx={{ fontSize: 16 }} />
              </Box>
              <Box
                sx={{
                  backgroundColor: '#F5F5F7',
                  padding: '12px 16px',
                  borderRadius: '20px 20px 20px 4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <CircularProgress size={16} sx={{ color: '#111111' }} />
                <Typography variant="caption" sx={{ color: '#757575', fontSize: '12px' }}>
                  Extracting document...
                </Typography>
              </Box>
            </Box>
          </Fade>
        )}

        {/* End Conversation Button inside Chat Body */}
        {messages.length > 0 &&
          !messages.some(m => m.state === 'CONFIRM_CLEAR_CHAT' || m.state === 'CONFIRM_END_CONVERSATION') && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 1 }}>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={onEndConversation}
                sx={{
                  textTransform: 'none',
                  borderRadius: '8px',
                  fontSize: '12.5px',
                  fontWeight: 600,
                  padding: '6px 16px',
                  borderColor: '#FFCDD2',
                  backgroundColor: '#FFF8F8',
                  '&:hover': {
                    borderColor: '#D32F2F',
                    backgroundColor: '#FFEBEE',
                  },
                  '&:focus-visible': {
                    outline: '2px solid #D32F2F',
                    outlineOffset: '2px',
                  },
                }}
              >
                End Conversation
              </Button>
            </Box>
          )}

        <div ref={messagesEndRef} />
      </Box>
    );
  },
);

ChatMessages.displayName = 'ChatMessages';
